import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatTimeAgo } from '@/shared/utils/formatTimeAgo';
import i18n from '@/core/i18n/config';
import { Button } from '@/shared/components/ui/button';
import { NoteOptionsMenu } from './NoteOptionsMenu';
import { ConvertToTaskDialog } from './ConvertToTaskDialog';
import type { Note, NoteExportFormat } from '../types';

interface NoteToolbarProps {
  note: Note;
  isSaving: boolean;
  onOpenHistory: () => void;
  onDelete: () => void;
  onExport: (format: NoteExportFormat) => void;
}

export function NoteToolbar({
  note,
  isSaving,
  onOpenHistory,
  onDelete,
  onExport,
}: NoteToolbarProps) {
  const { t } = useTranslation('notes');
  const [showConvertDialog, setShowConvertDialog] = useState(false);

  return (
    <>
      <div className="flex items-center justify-between gap-2 border-b border-border px-6 py-3 md:px-10">
        <span
          className="truncate text-xs text-muted-foreground"
          data-testid="notes-editor-saving-indicator"
          aria-live="polite"
        >
          {isSaving
            ? t('editor.saving')
            : t('editor.editedAgo', { time: formatTimeAgo(note.updatedAt, i18n.language) })}
        </span>
        <div className="flex items-center gap-2">
          <NoteOptionsMenu onOpenHistory={onOpenHistory} onDelete={onDelete} onExport={onExport} />
          <Button
            variant="default"
            size="sm"
            onClick={() => setShowConvertDialog(true)}
            data-testid="notes-convert-to-task-button"
          >
            {t('convertToTask.button')}
          </Button>
        </div>
      </div>

      <ConvertToTaskDialog
        open={showConvertDialog}
        onOpenChange={setShowConvertDialog}
        noteId={note.id ?? ''}
        noteTitle={note.title}
        noteContent={note.content}
      />
    </>
  );
}
